import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { MomentCarouselItem } from "../../data/momentOptions";
import { createMomentCoupon } from "../../data/momentOptions";
import CouponPreview from "./CouponPreview";

type Props = {
  carouselItems: MomentCarouselItem[];
  moreButtonEnabled: boolean;
};

export default function CarouselFeedPreview({ carouselItems, moreButtonEnabled }: Props) {
  const visibleItems = carouselItems.length > 0 ? carouselItems.slice(0, 6) : [
    { id: "p1", messageTitle: "", description: "내용 1", imagePreview: null, imageName: null, imageFile: null, imageUploadedUrl: "", pcLandingUrl: "", mobileLandingUrl: "", buttonTitle: "", buttonPcLandingUrl: "", buttonMobileLandingUrl: "", secondaryButtonTitle: "", secondaryButtonPcLandingUrl: "", secondaryButtonMobileLandingUrl: "", secondaryButtonEnabled: false, priceAmount: "", discountedPriceAmount: "", shareFlag: false, couponEnabled: false, coupon: createMomentCoupon() },
    { id: "p2", messageTitle: "", description: "내용 2", imagePreview: null, imageName: null, imageFile: null, imageUploadedUrl: "", pcLandingUrl: "", mobileLandingUrl: "", buttonTitle: "", buttonPcLandingUrl: "", buttonMobileLandingUrl: "", secondaryButtonTitle: "", secondaryButtonPcLandingUrl: "", secondaryButtonMobileLandingUrl: "", secondaryButtonEnabled: false, priceAmount: "", discountedPriceAmount: "", shareFlag: false, couponEnabled: false, coupon: createMomentCoupon() },
  ];

  const totalSlides = visibleItems.length + (moreButtonEnabled ? 1 : 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartXRef = useRef<number | null>(null);

  useEffect(() => {
    if (currentIndex > Math.max(totalSlides - 1, 0)) {
      setCurrentIndex(Math.max(totalSlides - 1, 0));
    }
  }, [currentIndex, totalSlides]);
  const slideWidth = 240;
  const gap = 8;
  const viewportWidth = 272;
  const moreWidth = moreButtonEnabled ? 72 : 0;
  const normalOffset = currentIndex * (slideWidth + gap);
  const fullTrackWidth =
    visibleItems.length * slideWidth +
    Math.max(visibleItems.length - 1, 0) * gap +
    (moreButtonEnabled ? gap + moreWidth : 0);
  const endAlignedOffset = Math.max(fullTrackWidth - viewportWidth, 0);
  const trackOffset =
    moreButtonEnabled && currentIndex === totalSlides - 1
      ? endAlignedOffset
      : normalOffset;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = e.clientX;
    setDragOffset(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null) return;
    setDragOffset(e.clientX - dragStartXRef.current);
  };

  const handlePointerEnd = () => {
    if (dragStartXRef.current === null) return;
    if (dragOffset <= -40) {
      setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    } else if (dragOffset >= 40) {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
    dragStartXRef.current = null;
    setDragOffset(0);
  };

  return (
    <div className="tw-text-[13px] tw-leading-relaxed">
      <div
        className="tw-py-2 tw-w-[272px] tw-overflow-hidden tw-cursor-grab active:tw-cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div
          className="tw-flex tw-gap-2 tw-transition-transform tw-duration-300 tw-ease-out"
          style={{ transform: `translateX(${-trackOffset + dragOffset}px)` }}
        >
          {visibleItems.map((item) => (
            <div key={item.id} className="tw-w-60 tw-shrink-0 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-shadow-sm">
              <div className="tw-aspect-square tw-bg-gray-50 tw-relative">
                {item.imagePreview ? (
                  <img src={item.imagePreview} alt="" draggable={false} className="tw-w-full tw-h-full tw-object-cover tw-select-none" />
                ) : (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-sm">
                    이미지
                  </div>
                )}
              </div>
              <div className={`tw-p-3 ${item.couponEnabled ? "tw-pb-4" : ""}`}>
                {item.messageTitle && (
                  <p className="tw-font-medium tw-text-sm tw-mb-1 tw-whitespace-pre-line tw-break-all">{item.messageTitle}</p>
                )}
                <p className="tw-text-xs tw-text-gray-600 tw-whitespace-pre-line tw-break-all">
                  {item.description || "내용"}
                </p>
                <div className={`tw-mt-3 ${item.shareFlag || item.secondaryButtonEnabled ? "tw-flex tw-gap-1" : ""}`}>
                  <div className={`${item.shareFlag || item.secondaryButtonEnabled ? "tw-flex-1" : "tw-w-full"} tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-text-[10px] tw-text-center tw-font-bold tw-text-gray-700`}>
                    {item.buttonTitle || "버튼1"}
                  </div>
                  {item.shareFlag && (
                    <div className="tw-flex-1 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-text-[10px] tw-text-center tw-font-bold tw-text-[#3a1d96]">
                      공유하기
                    </div>
                  )}
                  {!item.shareFlag && item.secondaryButtonEnabled && (
                    <div className="tw-flex-1 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-text-[10px] tw-text-center tw-font-bold tw-text-gray-700">
                      {item.secondaryButtonTitle || "버튼2"}
                    </div>
                  )}
                </div>
                <CouponPreview couponEnabled={item.couponEnabled} coupon={item.coupon} compact />
              </div>
            </div>
          ))}
          {moreButtonEnabled && (
            <div className="tw-w-[72px] tw-shrink-0 tw-h-[237px] tw-flex tw-items-center tw-justify-center">
              <button
                type="button"
                className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-border-0 tw-bg-transparent tw-p-0 tw-shadow-none tw-outline-none tw-ring-0 tw-text-gray-600"
              >
                <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-400">
                  <ArrowRight className="tw-h-5 tw-w-5 tw-text-white" />
                </span>
                <span className="tw-text-sm tw-font-semibold tw-text-gray-500">더보기</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {totalSlides > 1 && (
        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-w-60">
          <button
            type="button"
            onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
            disabled={currentIndex === 0}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-600 disabled:tw-opacity-40"
          >
            <ArrowLeft className="tw-w-3.5 tw-h-3.5" />
            이전
          </button>
          <span className="tw-text-xs tw-text-gray-500">
            {currentIndex + 1} / {totalSlides}
          </span>
          <button
            type="button"
            onClick={() => setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1))}
            disabled={currentIndex === totalSlides - 1}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-600 disabled:tw-opacity-40"
          >
            다음
            <ArrowRight className="tw-w-3.5 tw-h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
